import { FilterDescriptor, process } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useConfirm from 'src/hooks/useConfirm';
import useTransferListActions from 'src/hooks/useTransferListActions';
import useWindowSize from 'src/hooks/useWindowSize';
import { SetState } from 'src/types/generic';
import List from './List';
import SearchBox from './design-system/SearchBox';

export interface DefaultTransferListDataType {
  selected: boolean;
  isDirty: boolean;
  name: string;
  id: number;
  rowId: string;
  isDeleted: boolean;
}

interface TransferListActionsProps<D extends DefaultTransferListDataType> {
  leftList: D[];
  rightList: D[];
  setLeftList: SetState<D[]>;
  setRightList: SetState<D[]>;
  filteredLeftList: D[];
  filteredRightList: D[];
  label: string;
}

const TransferListActions = <D extends DefaultTransferListDataType>({
  leftList,
  rightList,
  setLeftList,
  setRightList,
  label,
  filteredLeftList,
  filteredRightList,
}: TransferListActionsProps<D>) => {
  const { tools } = useTransferListActions(
    leftList,
    rightList,
    filteredLeftList,
    filteredRightList,
    setLeftList,
    setRightList,
    label
  );

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      {tools.map((tool) => (
        <Tooltip anchorElement="target" position="bottom" parentTitle key={tool.id}>
          <span title={tool.tooltip}>
            <Button
              className="tool-button"
              disabled={tool.disabled}
              icon={tool.icon}
              onClick={tool.onClick}
              size={'small'}
            />
          </span>
        </Tooltip>
      ))}
    </div>
  );
};

export interface TransferListCategoryOption {
  value: number;
  label: string;
  filters: FilterDescriptor[];
}

interface Props<D extends DefaultTransferListDataType> {
  title: string;
  searchPlaceholder: string;
  leftListTitle: string;
  rightListTitle: string;
  leftList: D[];
  rightList: D[];
  setLeftList: SetState<D[]>;
  setRightList: SetState<D[]>;
  height: string;
  categories: TransferListCategoryOption[];
  label: string;
  deletedItemLabel?: string;
  isSidePanel?: boolean;
}

export const resetTransferList = <D extends DefaultTransferListDataType>(
  setLeftList: SetState<D[]>,
  setRightList: SetState<D[]>,
  leftList: D[],
  rightList: D[]
) => {
  setLeftList([
    ...leftList
      .filter((item) => !item.isDirty)
      .map((item) => ({ ...item, selected: false, isDirty: false })),
    ...rightList
      .filter((item) => item.isDirty)
      .map((item) => ({ ...item, selected: false, isDirty: false })),
  ]);
  setRightList([
    ...rightList
      .filter((item) => !item.isDirty)
      .map((item) => ({ ...item, selected: false, isDirty: false })),
    ...leftList
      .filter((item) => item.isDirty)
      .map((item) => ({ ...item, selected: false, isDirty: false })),
  ]);
};

function TransferList<D extends DefaultTransferListDataType>({
  title,
  searchPlaceholder,
  leftList,
  leftListTitle,
  setLeftList,
  rightList,
  rightListTitle,
  setRightList,
  height = '100px',
  categories,
  label,
  deletedItemLabel,
  isSidePanel = false,
}: Props<D>) {
  const defaultFilter = categories[1];
  const { height: windowHeight } = useWindowSize();
  const [search, setSearch] = useState('');
  const { confirm } = useConfirm();
  const { t } = useTranslation();
  const [filter, setFilter] = useState(defaultFilter);
  const { data: filteredLeftList } = useMemo(
    () =>
      process(
        leftList.filter((value) => value?.name?.toLowerCase()?.includes(search?.toLowerCase())),
        {
          filter: { filters: filter.filters, logic: 'and' },
          sort: [{ field: 'name', dir: 'asc' }],
        }
      ) as { data: D[] },
    [filter.filters, leftList, search]
  );

  const { data: filteredRightList } = useMemo(
    () =>
      process(
        rightList.filter((value) => value?.name?.toLowerCase()?.includes(search?.toLowerCase())),
        {
          filter: { filters: filter.filters, logic: 'and' },
          sort: [{ field: 'name', dir: 'asc' }],
        }
      ) as { data: D[] },
    [filter.filters, rightList, search]
  );

  const confirmBeforeProceeding = useCallback(
    (acceptCallback: VoidFunction) => {
      const isDirty =
        leftList.some((item) => item.isDirty) || rightList.some((item) => item.isDirty);
      if (isDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'Unsaved changes will be lost by proceeding. Cancel to return and save your changes.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
            resetTransferList(setLeftList, setRightList, leftList, rightList);
          }
        });
      } else {
        acceptCallback();
      }
    },
    [confirm, leftList, rightList, setLeftList, setRightList]
  );

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      confirmBeforeProceeding(() => {
        setFilter(e.value);
      });
    },
    [confirmBeforeProceeding]
  );

  return (
    <div className="flex items-center justify-start w-full flex-col">
      <div className="flex items-center justify-between gap-3 px-4 py-1 bg-[#3F6578] w-full">
        <div className="flex items-center gap-2 ">
          <h2 className="text-white">{t(title)}</h2>
          <SearchBox
            placeholder={searchPlaceholder}
            size={'small'}
            width={'200px'}
            value={search}
            onChange={setSearch}
          />
        </div>
        <DropDownList
          id="view-drop-down-list"
          style={{ width: 200 }}
          data={categories}
          textField="label"
          dataItemKey="value"
          value={filter}
          onChange={handleFilterChange}
        />
      </div>
      <div
        className="flex flex-row w-full gap-2 workflow-height"
        style={{ height: `${isSidePanel ? `${windowHeight - 370}px` : 'auto'}` }}
      >
        <List
          items={leftList}
          title={leftListTitle}
          setItems={setLeftList}
          height={height}
          filteredList={filteredLeftList}
          deletedItemLabel={deletedItemLabel}
        />
        <TransferListActions
          leftList={leftList}
          rightList={rightList}
          filteredLeftList={filteredLeftList}
          filteredRightList={filteredRightList}
          setLeftList={setLeftList}
          setRightList={setRightList}
          label={label}
        />
        <List
          items={rightList}
          title={rightListTitle}
          setItems={setRightList}
          height={height}
          filteredList={filteredRightList}
          deletedItemLabel={deletedItemLabel}
        />
      </div>
    </div>
  );
}
export default TransferList;
